import React from 'react';
import PropTypes from 'prop-types';
import { Table, Typography } from 'antd';
import { processingTime } from './CommunicationProcessingPanel';
import FactSyncRetryButton from './FactSyncRetryButton';

const EVENTS = {
  COURSE_COMPLETED: '课程完成',
  COURSE_RPE_UPDATED: '课后 RPE 更新',
  LIVE_CLASS_SUMMARY_UPDATED: '1V1 课程摘要更新',
  unknown: '未知事件',
};
const STATES = {
  pending: '等待同步',
  running: '同步中',
  succeeded: '同步成功',
  failed: '同步失败',
  unknown: '未知状态',
};
const validTime = (v) =>
  v == null || (Number.isSafeInteger(v) && v > 0 && v <= 8640000000000000);
const validId = (id) =>
  typeof id === 'string' &&
  /^[1-9]\d{0,18}$/.test(id) &&
  (id.length < 19 || id <= '9223372036854775807');
const lowerId = (id, before) =>
  id.length < before.length || (id.length === before.length && id < before);

export function validateFactSync(tasks, hasMore) {
  if (tasks == null && hasMore == null) return;
  if (
    !Array.isArray(tasks) ||
    typeof hasMore !== 'boolean' ||
    tasks.length > 20 ||
    (hasMore && tasks.length !== 20)
  )
    throw new Error('Invalid fact sync list');
  tasks.forEach((task, index) => {
    if (
      !task ||
      !validId(task.id) ||
      (index > 0 && !lowerId(task.id, tasks[index - 1].id)) ||
      !Object.prototype.hasOwnProperty.call(EVENTS, task.eventType) ||
      !Object.prototype.hasOwnProperty.call(STATES, task.status) ||
      (task.attemptCount != null &&
        (!Number.isInteger(task.attemptCount) ||
          task.attemptCount < 0 ||
          task.attemptCount > 2147483647)) ||
      ![
        task.changedAt,
        task.createdAt,
        task.startedAt,
        task.finishedAt,
        task.updatedAt,
      ].every(validTime)
    )
      throw new Error('Invalid fact sync task');
  });
}

export default function FactSyncProcessingSummary({
  tasks,
  hasMore,
  memberId,
  roomId,
}) {
  return (
    <>
      <Typography.Paragraph strong>训练事实同步</Typography.Paragraph>
      {tasks == null ? (
        <Typography.Paragraph type="secondary">
          当前服务尚未提供同步状态，不能据此判断有无任务。
        </Typography.Paragraph>
      ) : (
        <>
          {!tasks.length ? (
            <Typography.Paragraph>
              未查到本会员本课程的事实同步任务；可能未入队或不属于这条同步链路，不代表已同步完成。
            </Typography.Paragraph>
          ) : (
            <Table
              size="small"
              rowKey="id"
              dataSource={tasks}
              pagination={false}
              scroll={{ x: 1350 }}
              columns={[
                {
                  title: '操作',
                  key: 'retry',
                  render: (_, task) => (
                    <FactSyncRetryButton
                      key={`${memberId}:${roomId}:${task.id}:${task.updatedAt}:${task.attemptCount}`}
                      task={task}
                      memberId={memberId}
                      roomId={roomId}
                    />
                  ),
                },
                { title: '任务记录 ID', dataIndex: 'id' },
                {
                  title: '事件',
                  dataIndex: 'eventType',
                  render: (v) => EVENTS[v],
                },
                {
                  title: '同步状态',
                  dataIndex: 'status',
                  render: (v) => STATES[v],
                },
                {
                  title: '执行尝试次数',
                  dataIndex: 'attemptCount',
                  render: (v) => v ?? '未提供',
                },
                ...[
                  ['changedAt', '源事件时间'],
                  ['createdAt', '入队时间'],
                  ['startedAt', '最近开始'],
                  ['finishedAt', '最近结束'],
                  ['updatedAt', '最近更新'],
                ].map(([dataIndex, title]) => ({
                  title: `${title}（UTC+8）`,
                  dataIndex,
                  render: processingTime,
                })),
              ]}
            />
          )}
          {hasMore && (
            <Typography.Paragraph type="warning">
              仅展示按入队记录倒序的最近 20
              条，还有更早记录；不能作为整节课全部同步成功的判断。
            </Typography.Paragraph>
          )}
          <Typography.Paragraph type="secondary">
            每条记录对应一个源事件。同步成功不代表智能结论或标签已生成；失败不表示一定会自动重试。两个阶段分别读取，不是同一事务快照。查询不修改任务；只有明确确认重试才会重新入队。
          </Typography.Paragraph>
        </>
      )}
    </>
  );
}
FactSyncProcessingSummary.propTypes = {
  memberId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    .isRequired,
  roomId: PropTypes.number.isRequired,
  tasks: PropTypes.arrayOf(PropTypes.object),
  hasMore: PropTypes.bool,
};
FactSyncProcessingSummary.defaultProps = { tasks: null, hasMore: null };
